import { useState } from "react";
import { CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { UserProfile } from "@/types/user";
import ChatHeader from "./ChatHeader";
import ChatMessagesContainer from "./ChatMessagesContainer";
import ChatMessageInput from "./ChatMessageInput";
import ChatProfilePicDialog from "./ChatProfilePicDialog";
import { ChatType, ChatMessage } from "./types";

type ChatWindowProps = {
  recipient: UserProfile;
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  currentUserId: string;
  onProfilePicChange?: (userId: string, newAvatarUrl: string) => void;
  chatType?: ChatType;
  members?: UserProfile[];
  groupName?: string;
  onInviteMembers?: () => void;
};

export default function ChatWindow({
  recipient,
  messages,
  onSendMessage,
  currentUserId,
  onProfilePicChange,
  chatType = "individual",
  members = [],
  groupName,
  onInviteMembers,
}: ChatWindowProps) {
  const [isChangeAvatarOpen, setIsChangeAvatarOpen] = useState(false);

  // Ensure we have a non-null recipient
  if (!recipient) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <p className="text-muted-foreground">No recipient selected</p>
      </div>
    );
  }

  // Ensure avatar URL for recipient
  const recipientAvatar =
    recipient.avatarUrl ||
    `https://api.dicebear.com/7.x/personas/svg?seed=${recipient.name}`;

  const handleProfileUpdate = (newAvatarUrl: string) => {
    if (onProfilePicChange) {
      onProfilePicChange(recipient.id, newAvatarUrl);
    }
  };

  const isWelcomeChat = recipient.name === "Welcome Bot";

  return (
    <div className="flex flex-col h-full">
      <CardHeader className="border-b flex-none bg-card p-0">
        <ChatHeader
          recipient={recipient}
          onOpenProfilePicDialog={() => setIsChangeAvatarOpen(true)}
          chatType={chatType}
          members={members}
          groupName={groupName}
        />
      </CardHeader>

      <ChatMessagesContainer
        messages={messages}
        currentUserId={currentUserId}
        recipientName={chatType === "group" ? groupName : recipient.name}
        groupMembers={chatType === "group" ? members : undefined}
        onInviteMembers={onInviteMembers}
      />

      <CardFooter className="p-4 border-t flex-none bg-card">
        <ChatMessageInput onSendMessage={onSendMessage} isWelcomeChat={isWelcomeChat} />
      </CardFooter>

      {chatType === "individual" && !isWelcomeChat && (
        <ChatProfilePicDialog
          isOpen={isChangeAvatarOpen}
          onOpenChange={setIsChangeAvatarOpen}
          currentAvatarUrl={recipientAvatar}
          onUpdateAvatar={handleProfileUpdate}
        />
      )}
    </div>
  );
}
